import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const FeedbackWidget: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !feedback.trim()) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const { error } = await supabase.from('feedback').insert({
        user_id: user.id,
        user_email: user.email,
        feedback_text: feedback.trim(),
        page_url: window.location.href,
      });

      if (error) throw error;

      setSubmitStatus('success');
      setFeedback('');

      setTimeout(() => {
        setIsOpen(false);
        setSubmitStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't show if user is not authenticated
  if (!user) return null;

  return (
    <>
      {/* Feedback Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-charcoal px-4 py-3 text-sm font-medium text-white shadow-lift transition-all duration-200 ease-out-expo hover:-translate-y-0.5"
        aria-label="Send feedback"
      >
        {isOpen ? (
          <>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Close
          </>
        ) : (
          <>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            Feedback
          </>
        )}
      </button>

      {/* Feedback Form */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.25, ease: EASE }}
            className="card-frame fixed bottom-24 right-6 z-50 w-80 overflow-hidden !bg-white"
          >
            <div className="border-b border-hairline px-5 py-4">
              <span className="eyebrow">Feedback</span>
              <h3 className="mt-1 font-serif text-lg text-charcoal">Help us improve Steelman</h3>
            </div>

            <form onSubmit={handleSubmit} className="p-5">
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Tell us what you think…"
                className="w-full resize-none rounded-lg border border-black/[0.08] bg-white px-3.5 py-2.5 text-sm text-charcoal placeholder-ink-muted/60 transition-colors focus:border-charcoal/30 focus:outline-none focus:ring-2 focus:ring-charcoal/10"
                rows={5}
                disabled={isSubmitting || submitStatus === 'success'}
                required
              />

              <AnimatePresence>
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <p className="mt-3 flex items-center gap-2 rounded-lg border border-black/[0.08] bg-charcoal/[0.04] px-3.5 py-2.5 text-sm font-medium text-charcoal">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      Thanks for your feedback!
                    </p>
                  </motion.div>
                )}
                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <p className="mt-3 rounded-lg border border-vermilion/30 bg-vermilion/[0.06] px-3.5 py-2.5 text-sm font-medium text-vermilion-deep">
                      Failed to submit. Please try again.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="btn-secondary flex-1 !py-2.5"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !feedback.trim() || submitStatus === 'success'}
                  className="btn-primary flex-1 !py-2.5 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                >
                  {isSubmitting ? 'Sending…' : 'Send'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FeedbackWidget;
